import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";

import { WFPButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { OfferSummary } from "../OfferSummary";
import { Offer } from "../OfferCard/interfaces";

export interface SendingBackFormValues {
  send_back_comments: string;
}

interface SendingBackExchangeModalProps {
  offer?: Offer;
  isOpen: boolean;
  onCancel: () => void;
  onValidateSendingBack: (offer: Offer, values: SendingBackFormValues) => Promise<unknown>;
}

const DEFAULT_VALUES: SendingBackFormValues = { send_back_comments: "" };

export const SendingBackExchangeModal = ({
  offer,
  isOpen,
  onCancel,
  onValidateSendingBack,
}: SendingBackExchangeModalProps) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    register,
    reset,
    formState: { isSubmitting },
  } = useForm<SendingBackFormValues>({ defaultValues: DEFAULT_VALUES });

  useEffect(() => {
    if (isOpen) {
      reset(DEFAULT_VALUES);
    }
  }, [isOpen, reset]);

  const onSubmit = handleSubmit(values =>
    offer ? onValidateSendingBack(offer, values) : undefined
  );

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onCancel()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("sendingBackExchangeModal.heading")}</DialogTitle>
          <DialogDescription>{t("sendingBackExchangeModal.instructionText")}</DialogDescription>
        </DialogHeader>

        {offer && <OfferSummary offer={offer} />}

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="send_back_comments" className="font-semibold text-foreground">
              {t("sendingBackExchangeModal.comments")}
            </Label>
            <Textarea id="send_back_comments" {...register("send_back_comments")} />
          </div>

          <DialogFooter>
            <WFPButton type="button" variant="secondary" onClick={onCancel}>
              {t("common.cancel")}
            </WFPButton>
            <WFPButton type="submit" disabled={isSubmitting}>
              {t("sendingBackExchangeModal.submitButtonText")}
            </WFPButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
